import axios from 'axios'
import type { AppData, Project } from '../types/data'

// 获取 API 基础 URL
// 优先使用环境变量，否则使用相对路径（自动使用当前域名）
// 这样在跨机器访问时，会自动使用目标服务器的地址
const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL
  }
  // 使用相对路径，确保跨机器访问时使用正确的服务器地址
  return '/api'
}

const API_BASE_URL = getApiBaseUrl()

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 验证用户是否存在（通过检查是否有数据）
export const verifyUser = async (userUuid: string) => {
  try {
    // 使用 /data 接口检查用户是否存在
    const response = await api.get('/data', { params: { userUuid } })
    if (response.data.success && response.data.data) {
      return {
        success: true,
        message: '用户存在',
        data: {
          userUuid: userUuid,
          exists: true,
          userData: response.data.data // 返回用户数据，方便后续使用
        }
      }
    } else {
      return {
        success: false,
        message: '用户不存在',
        data: {
          userUuid: userUuid,
          exists: false
        }
      }
    }
  } catch (error: any) {
    // 如果是 404 或其他错误，说明用户不存在
    return {
      success: false,
      message: '用户不存在',
      data: {
        userUuid: userUuid,
        exists: false
      }
    }
  }
}

// 获取用户数据
export const getUserData = async (userUuid: string): Promise<AppData | null> => {
  try {
    const response = await api.get('/data', { params: { userUuid } })
    if (response.data.success && response.data.data) {
      return response.data.data
    }
    return null
  } catch (error) {
    console.error('获取数据失败:', error)
    return null
  }
}

// 保存用户数据
export const saveUserData = async (userUuid: string, data: AppData) => {
  try {
    const response = await api.post('/data', { userUuid, data })
    return response.data.success
  } catch (error) {
    console.error('保存数据失败:', error)
    return false
  }
}

// 获取单个项目
export const getProject = async (userUuid: string, projectUuid: string): Promise<Project | null> => {
  try {
    const response = await api.get('/project', { params: { userUuid, projectUuid } })
    if (response.data.success && response.data.data) {
      return response.data.data
    }
    return null
  } catch (error) {
    console.error('获取项目失败:', error)
    return null
  }
}

// 推送单个项目到服务器
export const pushProject = async (userUuid: string, project: Project) => {
  try {
    const response = await api.post('/project', { userUuid, project })
    return response.data
  } catch (error) {
    console.error('推送项目失败:', error)
    throw error
  }
}

// 更新项目中 Item 的分享链接
export const updateItemShareLink = async (targetUserUuid: string, projectUuid: string, itemUuid: string, shareLink: string) => {
  try {
    const response = await api.patch('/project/item/share-link', { 
        targetUserUuid, 
        projectUuid, 
        itemUuid, 
        shareLink 
    })
    return response.data
  } catch (error) {
    console.error('更新 Item 链接失败:', error)
    throw error
  }
}

// 删除项目
export const deleteProject = async (userUuid: string, projectUuid: string) => {
  try {
    const response = await api.delete('/project', { params: { userUuid, projectUuid } })
    return response.data
  } catch (error) {
    console.error('删除项目失败:', error)
    throw error
  }
}

// 上传文件
export const uploadFile = async (file: File, userUuid: string, projectId?: string, fileId?: string) => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('userUuid', userUuid)
  if (projectId) {
    formData.append('projectId', projectId)
  }
  if (fileId) {
    formData.append('fileId', fileId)
  }

  try {
    const response = await api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      params: {
        userUuid,
        ...(projectId && { projectId }),
        ...(fileId && { fileId })
      }
    })
    return response.data
  } catch (error) {
    console.error('上传文件失败:', error)
    throw error
  }
}

// 下载文件
export const downloadFile = (fileId: string, userUuid: string) => {
  return `${API_BASE_URL}/files/download?fileId=${fileId}&userUuid=${userUuid}`
}

// 创建分享链接
export const createShareLink = async (
  userUuid: string, 
  projectUuid: string, 
  expiresInDays?: number,
  allowDownload?: boolean,
  message?: string,
  password?: string,
  lockInterface?: boolean
) => {
  try {
    const response = await api.post('/share/create', { 
      userUuid, 
      projectUuid, 
      expiresInDays,
      allowDownload,
      message,
      password,
      lockInterface
    })
    return response.data
  } catch (error) {
    console.error('创建分享链接失败:', error)
    throw error
  }
}

// 获取分享链接信息
export const getShareLinkInfo = async (shareId: string) => {
  try {
    const response = await api.get('/share/info', { params: { shareId } })
    return response.data
  } catch (error) {
    console.error('获取分享链接失败:', error)
    throw error
  }
}

// 获取用户的分享链接列表
export const getUserShareLinks = async (userUuid: string, projectUuid?: string) => {
  try {
    const response = await api.get('/share/list', { params: { userUuid, projectUuid } })
    return response.data
  } catch (error) {
    console.error('获取分享链接列表失败:', error)
    throw error
  }
}

// 删除分享链接
export const deleteShareLink = async (userUuid: string, shareId: string) => {
  try {
    const response = await api.delete('/share/delete', { params: { userUuid, shareId } })
    return response.data
  } catch (error) {
    console.error('删除分享链接失败:', error)
    throw error
  }
}

// 通过分享链接获取项目数据（只读，需要密码验证）
export const getProjectByShareId = async (shareId: string, password?: string): Promise<Project | null> => {
  try {
    const response = await api.post(`/share/project/${shareId}`, { password })
    if (response.data.success && response.data.data) {
      return response.data.data
    }
    return null
  } catch (error) {
    console.error('通过分享链接获取项目失败:', error)
    return null
  }
}

// 检查分享链接是否需要密码
export const checkSharePassword = async (shareId: string) => {
  try {
    const response = await api.get(`/share/project/${shareId}/check`)
    return response.data
  } catch (error) {
    console.error('检查分享链接失败:', error)
    throw error
  }
}

// 存储配置类型
export interface StorageConfig {
  useOSS: boolean
  provider?: 'aliyun' | 'tencent'
  bucket: string | null
  region: string | null
  endpoint: string | null
}

// STS 凭证类型
export interface OssStsToken {
  provider?: 'aliyun' | 'tencent'
  credentials: {
    accessKeyId: string
    accessKeySecret: string
    securityToken: string
    expiration: string | number
  }
  bucket: string
  region: string
  endpoint: string
}

// 获取存储配置
export const getStorageConfig = async (): Promise<StorageConfig | null> => {
  try {
    console.log('📦 [api] 请求存储配置: GET /config/storage')
    const response = await api.get('/config/storage')
    console.log('📦 [api] 存储配置响应:', response.data)
    if (response.data.success && response.data.data) {
      console.log('📦 [api] 存储配置数据:', response.data.data)
      return response.data.data
    }
    console.warn('📦 [api] 存储配置响应格式异常')
    return null
  } catch (error) {
    console.error('❌ [api] 获取存储配置失败:', error)
    return null
  }
}

// 获取 OSS STS 临时凭证
export const getOssStsToken = async (): Promise<OssStsToken | null> => {
  try {
    const response = await api.get('/oss/sts-token')
    if (response.data.success && response.data.data) {
      return response.data.data
    }
    return null
  } catch (error) {
    console.error('获取 OSS STS 凭证失败:', error)
    return null
  }
}

// 上传配置类型
export interface UploadConfig {
  maxFileSize: number
  maxFileSizeFormatted: string
  maxFiles: number
  allowedMimeTypes: string[]
  imageCompressionThreshold: number
  imageCompressionThresholdFormatted: string
}

// 获取上传配置
export const getUploadConfig = async (): Promise<UploadConfig | null> => {
  try {
    const response = await api.get('/files/config')
    if (response.data.success && response.data.data) {
      return response.data.data
    }
    return null
  } catch (error) {
    console.error('获取上传配置失败:', error)
    return null
  }
}

// 通过 userUuid 更新用户信息（不需要token）
export const updateUserInfoByUuid = async (
  userUuid: string,
  updates: { email?: string; nickname?: string; avatar?: string }
) => {
  try {
    const response = await api.put('/user/update', { userUuid, ...updates })
    return response.data
  } catch (error) {
    console.error('更新用户信息失败:', error)
    throw error
  }
}

// 通过 userUuid 获取用户信息（不需要token）
export const getUserInfoByUuid = async (userUuid: string) => {
  try {
    const response = await api.get('/user/info', { params: { userUuid } })
    return response.data
  } catch (error) {
    console.error('获取用户信息失败:', error)
    throw error
  }
}

// 获取用户设置
export const getUserSettings = async (userUuid: string) => {
  try {
    const response = await api.get('/user/settings', { params: { userUuid } })
    if (response.data.success && response.data.data) {
      return response.data.data
    }
    return null
  } catch (error) {
    console.error('获取用户设置失败:', error)
    return null
  }
}

// 保存用户设置
export const saveUserSettings = async (userUuid: string, settings: any) => {
  try {
    const response = await api.post('/user/settings', { userUuid, settings })
    return response.data.success
  } catch (error) {
    console.error('保存用户设置失败:', error)
    return false
  }
}

// ============ 新用户认证系统 API ============

// 认证响应类型
export interface AuthResponse {
  success: boolean
  message: string
  data?: {
    user: {
      userUuid: string
      username: string
      phone?: string
      role: string
      email?: string
      nickname?: string
      avatar?: string
      hasPassword?: boolean
    }
    token: string
    refreshToken: string
    isNewUser?: boolean
  }
}

// 用户注册（用户名+手机号+验证码，不使用密码）
export const registerUser = async (params: {
  username: string
  phone: string
  code: string
  email?: string
  nickname?: string
  avatar?: string
}): Promise<AuthResponse> => {
  try {
    const response = await api.post('/user/register', params)
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || '注册失败'
    }
  }
}

// 用户登录（用户名/手机号+验证码，不使用密码）
export const loginUser = async (params: {
  identifier: string
  code: string
}): Promise<AuthResponse> => {
  try {
    const response = await api.post('/user/login', params)
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || '登录失败'
    }
  }
}

// 发送短信验证码
export const sendSmsCode = async (params: {
  phone: string
  type?: 'login' | 'register' | 'reset_password' | 'find_uuid' | 'bind_phone'
}): Promise<{ success: boolean; message: string; data?: { code?: string; expiresIn: number } }> => {
  try {
    const response = await api.post('/user/sms/send', params)
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || '发送验证码失败'
    }
  }
}

// 短信登录（自动注册）
export const loginWithSms = async (params: {
  phone: string
  code: string
}): Promise<AuthResponse> => {
  try {
    const response = await api.post('/user/login/sms', params)
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || '短信登录失败'
    }
  }
}

// 通过手机号找回 UUID
export const findUuidByPhone = async (params: {
  phone: string
  code: string
}): Promise<{ success: boolean; message: string; data?: { userUuid: string; username: string; nickname?: string } }> => {
  try {
    const response = await api.post('/user/find-uuid', params)
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || '找回UUID失败'
    }
  }
}

// 检查用户名是否可用
export const checkUsername = async (username: string): Promise<{ success: boolean; data?: { available: boolean; reason?: string } }> => {
  try {
    const response = await api.get('/user/check-username', { params: { username } })
    return response.data
  } catch (error: any) {
    return {
      success: false,
      data: { available: false, reason: '检查失败' }
    }
  }
}

// 修改手机号（需要登录，需要验证码）
export const changePhone = async (params: {
  newPhone: string
  code: string
}, token: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post('/user/phone/change', params, {
      headers: { Authorization: `Bearer ${token}` }
    })
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || '修改手机号失败'
    }
  }
}

// 修改用户名（需要登录）
export const changeUsername = async (params: {
  newUsername: string
}, token: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post('/user/username/change', params, {
      headers: { Authorization: `Bearer ${token}` }
    })
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || '修改用户名失败'
    }
  }
}

// 找回账号（通过手机号）
export const forgotAccount = async (params: {
  phone: string
  code: string
}): Promise<{ success: boolean; message: string; data?: { userUuid: string; username: string; nickname?: string } }> => {
  try {
    const response = await api.post('/user/forgot-account', params)
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || '找回账号失败'
    }
  }
}

// 绑定手机号
export const bindPhone = async (params: {
  phone: string
  code: string
}, token: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post('/user/bindPhone', params, {
      headers: { Authorization: `Bearer ${token}` }
    })
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || '绑定手机号失败'
    }
  }
}

// 设置密码（首次设置）
export const setPassword = async (params: {
  newPassword: string
}, token: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post('/user/password/set', params, {
      headers: { Authorization: `Bearer ${token}` }
    })
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || '设置密码失败'
    }
  }
}

// 修改密码
export const changePassword = async (params: {
  oldPassword: string
  newPassword: string
}, token: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post('/user/password/change', params, {
      headers: { Authorization: `Bearer ${token}` }
    })
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || '修改密码失败'
    }
  }
}

// 验证 token
export const verifyToken = async (token: string): Promise<{ success: boolean; data?: { user: any; valid: boolean } }> => {
  try {
    const response = await api.get('/user/verify', {
      headers: { Authorization: `Bearer ${token}` }
    })
    return response.data
  } catch (error: any) {
    return {
      success: false,
      data: { user: null, valid: false }
    }
  }
}

// 刷新 token
export const refreshAuthToken = async (refreshToken: string): Promise<{ success: boolean; data?: { token: string; user: any } }> => {
  try {
    const response = await api.post('/user/refresh-token', { refreshToken })
    return response.data
  } catch (error: any) {
    return {
      success: false
    }
  }
}

// 获取当前用户信息（需要 token）
export const getCurrentUserInfo = async (token: string) => {
  try {
    const response = await api.get('/user/me', {
      headers: { Authorization: `Bearer ${token}` }
    })
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || '获取用户信息失败'
    }
  }
}

// 查询手机号关联的所有账号（一个手机号可以绑定多个账号）
export const getAccountsByPhone = async (params: {
  phone: string
  code: string
}): Promise<{ success: boolean; message: string; data?: Array<{ userUuid: string; username: string; nickname?: string }> }> => {
  try {
    // 先验证验证码，然后查询关联账号
    // 注意：后端目前一个手机号只能绑定一个账号，这里返回单个账号作为数组
    const response = await api.post('/user/find-uuid', params)
    if (response.data.success && response.data.data) {
      return {
        success: true,
        message: '查询成功',
        data: [response.data.data] // 包装成数组
      }
    }
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || '查询失败'
    }
  }
}

export default api


